/**
 * Auto-update poller (tech-design §9 companion to the PWA flow). Each deploy drops
 * a fresh `version.json` (the git `<height>-<shorthash>` this build was cut from)
 * next to the hashed assets. The running client polls it on an interval and, when
 * the hash changes, saves the session and hard-reloads into the new build.
 *
 * This is the *automatic* path that complements the click-to-reload banner in
 * {@link ../pwa}. A plain `location.reload()` would be served the stale
 * precached app shell by the current service worker, so before reloading we pull
 * the newly deployed worker and let it take control — otherwise the "hard
 * refresh" would land right back on the old version.
 */
/// <reference types="vite/client" />

declare const __BUILD_VERSION__: string;

export interface VersionCheckHooks {
  /** Persist the session before reloading so nothing is lost. */
  readonly saveNow: () => Promise<void>;
  /** Poll cadence in milliseconds; defaults to 60s. */
  readonly intervalMs?: number;
}

/** The version this bundle was built at (git `<height>-<shorthash>`). */
export const BUILD_VERSION: string = __BUILD_VERSION__;

/**
 * `true` when a freshly fetched version differs from the running one. Pure and
 * exported so the decision is unit-testable without a DOM or network.
 */
export function isNewBuild(current: string, latest: unknown): boolean {
  return (
    typeof latest === 'string' &&
    latest.length > 0 &&
    current.length > 0 &&
    latest !== current
  );
}

/** Pull the version string out of a parsed `version.json` body, if present. */
function readVersion(body: unknown): unknown {
  if (body && typeof body === 'object' && 'version' in body) {
    return body.version;
  }
  return undefined;
}

/**
 * Resolve to the worker that will next control the page (the one just fetched by
 * `registration.update()`), or `null` if there is nothing new to activate.
 */
function waitForWaiting(reg: ServiceWorkerRegistration): Promise<ServiceWorker | null> {
  if (reg.waiting) return Promise.resolve(reg.waiting);
  const installing = reg.installing;
  if (!installing) return Promise.resolve(null);
  return new Promise((resolve) => {
    installing.addEventListener('statechange', () => {
      if (installing.state === 'installed') resolve(reg.waiting ?? installing);
      else if (installing.state === 'redundant') resolve(null);
    });
  });
}

/**
 * Best-effort hard refresh into the newest deploy. When a service worker is in
 * play we activate the freshly deployed one first (skip-waiting + reload on
 * `controllerchange`) so the reload isn't served the stale precache. Returns
 * `true` if a reload was initiated.
 */
async function activateLatestThenReload(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    window.location.reload();
    return true;
  }
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    window.location.reload();
    return true;
  }
  try {
    await reg.update(); // fetch the newly deployed sw.js
  } catch {
    // network hiccup — fall through; a later poll will retry
  }
  const waiting = await waitForWaiting(reg);
  if (!waiting) return false; // SW not caught up yet; retry on the next tick

  let reloaded = false;
  const reload = (): void => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };
  navigator.serviceWorker.addEventListener('controllerchange', reload, {
    once: true,
  });
  waiting.postMessage({ type: 'SKIP_WAITING' });
  window.setTimeout(reload, 3000); // safety net if controllerchange never fires
  return true;
}

/**
 * Start polling `version.json`. No-op outside production builds (there is no
 * emitted version file to read in dev).
 */
export function initVersionCheck(hooks: VersionCheckHooks): void {
  if (!import.meta.env.PROD) return;
  const intervalMs = hooks.intervalMs ?? 60_000;
  let reloading = false;

  const check = async (): Promise<void> => {
    if (reloading) return;
    let body: unknown;
    try {
      const res = await fetch(`version.json?ts=${String(Date.now())}`, {
        cache: 'no-store',
      });
      if (!res.ok) return;
      body = (await res.json()) as unknown;
    } catch {
      return; // offline or not deployed yet — retry next tick
    }
    if (!isNewBuild(BUILD_VERSION, readVersion(body))) return;

    reloading = true;
    await hooks.saveNow();
    // If the worker hasn't caught up we un-latch and try again next tick.
    reloading = await activateLatestThenReload();
  };

  window.setInterval(() => void check(), intervalMs);
}
